import { useState } from 'react';
import { Person } from './Avatar';
import { Band } from './Band';
import { downloadFile } from '../lib/download';
import { errorMessage } from '../lib/api';
import { Banner } from './Banner';
import type { SessionReport } from '../lib/sessions';

const band = (value: number | null) => (value === null ? '' : value.toFixed(1));

/**
 * What a session produced, best band first.
 *
 * Descending because that is the question being asked of it — who did well,
 * and how far down does the room go. Candidates who sat nothing stay on the
 * list, at the bottom, marked absent: dropping them would make a room of
 * thirty look like a room of twenty-six and hide the four people a centre most
 * needs to notice.
 */
export function SessionResults({
  report,
  downloadPath,
  heading = 'Results',
}: {
  report: SessionReport;
  /** The API path the spreadsheet of this same table comes from. */
  downloadPath: string;
  heading?: string;
}) {
  const { summary, rows } = report;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await downloadFile(downloadPath, `${report.session.day}-${heading}.csv`);
    } catch (err) {
      setError(errorMessage(err, 'That table could not be downloaded'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel stack">
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div>
          <h2 className="panel__title">{heading}</h2>
          <p className="muted">
            <span className="mono">{summary.sat}</span> of{' '}
            <span className="mono">{summary.seated}</span> sat
            {summary.absent > 0 && (
              <>
                {' '}
                · <span className="mono">{summary.absent}</span> absent
              </>
            )}
            {summary.awaitingWriting > 0 && (
              <>
                {' '}
                · <span className="mono">{summary.awaitingWriting}</span> awaiting marking
              </>
            )}
          </p>
        </div>
        <button className="btn" disabled={saving} onClick={() => void save()}>
          {saving ? 'Preparing…' : 'Download the table'}
        </button>
      </div>

      {error && <Banner tone="error">{error}</Banner>}

      {summary.average !== null && (
        <div className="stats">
          <div className="stat">
            <span className="stat__value mono">{summary.average.toFixed(1)}</span>
            <span className="stat__label">Average band</span>
          </div>
          <div className="stat">
            <span className="stat__value mono">{summary.highest?.toFixed(1)}</span>
            <span className="stat__label">Highest</span>
          </div>
          <div className="stat">
            <span className="stat__value mono">{summary.lowest?.toFixed(1)}</span>
            <span className="stat__label">Lowest</span>
          </div>
          <div className="stat">
            <span className="stat__value mono">{summary.sat}</span>
            <span className="stat__label">Sat the session</span>
          </div>
        </div>
      )}

      <div className="ledger__wrap">
        <table className="ledger">
          <thead>
            <tr>
              <th className="num">#</th>
              <th className="num">Candidate</th>
              <th>Name</th>
              <th>Test</th>
              <th className="num">Listening</th>
              <th className="num">Reading</th>
              <th className="num">Writing</th>
              <th className="num">Average</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.student.id} className={row.sat ? undefined : 'muted'}>
                <td className="num mono">{index + 1}</td>
                <td className="num">{row.student.candidateNumber ?? '—'}</td>
                <td>
                  <Person name={row.student.fullName} />
                </td>
                <td className="muted">{row.sitting ?? ''}</td>
                <td className="num mono">{band(row.listening)}</td>
                <td className="num mono">{band(row.reading)}</td>
                <td className="num mono">
                  {row.awaitingWriting ? (
                    <span className="tag">to mark</span>
                  ) : (
                    band(row.writing)
                  )}
                </td>
                <td className="num">
                  {row.sat ? <Band value={row.average} /> : <span className="tag tag--off">absent</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
